import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  InputField,
  Modal,
  Select,
  SelectionBox,
  SelectionBoxGroup,
  type InputFieldVariant,
} from '@enamelai/design-system'
import {
  MEMBERSHIP_BILLING_FREQUENCIES,
  MANAGE_PAYMENT_COUNTRIES,
  MANAGE_RECURRING_AUTO_PAY_METHODS,
  MEMBERSHIP_CYCLE_AMOUNT,
} from '@constants/Membership/membershipPage.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import type {
  MembershipAddMemberOption,
  MembershipPageMember,
  MembershipPaymentCycle,
} from '@dataTypes/membership.type'
import { updateMembershipMember } from '@store/actions/membership.action'
import { useAppDispatch } from '@store/hooks'
import { formatInputFieldCardNumber } from '@components/ui/InputField/InputField.utils'
import { detectCardBrandFromNumber, getCardLogo } from '@utils/cardBrand.util'
import {
  createFailedToast,
  createPaymentDetailsUpdatedToast,
} from '@utils/notificationToast.util'
import './ManageRecurringPaymentModal.css'

type ManageRecurringPaymentModalProps = {
  open: boolean
  member?: MembershipPageMember | null
  onClose: () => void
  onSaved?: (details: {
    member: MembershipPageMember
    billingLabel: string
    autoPayMethod: string
  }) => void
}

const toApiDate = (value: string): string => {
  const [month, day, year] = value.split('/')
  if (!month || !day || !year) return value
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

const formatSavedBilling = (member: MembershipPageMember): string => {
  const amount = member.recurringAmount ?? 0
  const label = Number.isInteger(amount)
    ? `$${amount}`
    : `$${amount.toFixed(2)}`
  return `${label} (${member.paymentCycle})`
}

const CYCLE_BY_FREQUENCY: Record<string, MembershipPaymentCycle> = {
  monthly: 'Monthly',
  annual: 'Annual',
}

const FREQUENCY_BY_CYCLE: Record<MembershipPaymentCycle, string> = {
  Monthly: 'monthly',
  Annual: 'annual',
}

const cloneAutoPayMethods = (): MembershipAddMemberOption[] =>
  MANAGE_RECURRING_AUTO_PAY_METHODS.map((option) => ({ ...option }))

/** DS InputField defaults to email; these payment fields are plain text. */
const PLAIN_TEXT_VARIANT = 'text' as InputFieldVariant

const getBrandLabel = (cardNumber: string): string => {
  const brand = detectCardBrandFromNumber(cardNumber)
  if (brand === 'unknown') return 'Visa'
  if (brand === 'amex') return 'Amex'
  return brand.charAt(0).toUpperCase() + brand.slice(1)
}

const formatExpiry = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`
}

const renderAccountDescription = (
  option: MembershipAddMemberOption,
): string | undefined =>
  option.lastFour
    ? `${option.descriptionPrefix ?? ''}${option.lastFour}`
    : option.description

const formatAutoPayLabel = (option: MembershipAddMemberOption): string => {
  const brand = (option.descriptionPrefix ?? 'Visa ending in ')
    .replace(/ ending in $/, '')
    .toLowerCase()

  return `${option.title} - ${brand} ending in ${option.lastFour}`
}

const ManageRecurringPaymentModal: React.FC<
  ManageRecurringPaymentModalProps
> = ({ open, member, onClose, onSaved }) => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const [saving, setSaving] = useState(false)
  const [isAddCardOpen, setIsAddCardOpen] = useState(false)
  const [frequency, setFrequency] = useState('monthly')
  const [autoPay, setAutoPay] = useState('debit')
  const [methods, setMethods] = useState(cloneAutoPayMethods)
  const [cardNumber, setCardNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvc, setCvc] = useState('')
  const [country, setCountry] = useState('US')
  const [zip, setZip] = useState('')
  const [isDefaultCard, setIsDefaultCard] = useState(true)

  const memberName = member?.name ?? 'Patient'

  const resetAddCardForm = (): void => {
    setCardNumber('')
    setExpiry('')
    setCvc('')
    setCountry('US')
    setZip('')
    setIsDefaultCard(true)
  }

  useEffect(() => {
    setIsAddCardOpen(false)
    setCardNumber('')
    setExpiry('')
    setCvc('')
    setCountry('US')
    setZip('')
    setIsDefaultCard(true)
    setAutoPay('debit')
    setMethods(cloneAutoPayMethods())

    if (!open) {
      setFrequency('monthly')
      return
    }

    setFrequency(
      FREQUENCY_BY_CYCLE[member?.paymentCycle ?? 'Monthly'] ?? 'monthly',
    )
  }, [open, member?.id, member?.paymentCycle])

  const cardDigits = cardNumber.replace(/\D/g, '')
  const cardBrand = detectCardBrandFromNumber(cardNumber)
  const expiryDigits = expiry.replace(/\D/g, '')
  const expiryMonth = Number(expiryDigits.slice(0, 2))
  const isAddCardValid =
    cardDigits.length >= 13 &&
    expiryDigits.length === 4 &&
    expiryMonth >= 1 &&
    expiryMonth <= 12 &&
    cvc.replace(/\D/g, '').length >= 3 &&
    Boolean(country) &&
    zip.replace(/\D/g, '').length >= 5

  const selectedMethod = useMemo(
    () => methods.find((option) => option.value === autoPay),
    [autoPay, methods],
  )

  const handleClose = (): void => {
    setIsAddCardOpen(false)
    onClose()
  }

  const handleOpenAddCard = (): void => {
    resetAddCardForm()
    setIsAddCardOpen(true)
  }

  const handleCloseAddCard = (): void => {
    setIsAddCardOpen(false)
    resetAddCardForm()
  }

  const handleCountryChange = (value: string | string[]): void => {
    setCountry(typeof value === 'string' ? value : (value[0] ?? ''))
  }

  const handleAddCard = (): void => {
    if (!isAddCardValid) return

    const lastFour = cardDigits.slice(-4)
    const brandLabel = getBrandLabel(cardNumber)
    const option: MembershipAddMemberOption = {
      value: `credit-${lastFour}-${Date.now()}`,
      title: 'Credit card',
      descriptionPrefix: `${brandLabel} ending in `,
      lastFour,
    }

    setMethods((current) => [...current, option])
    if (isDefaultCard) {
      setAutoPay(option.value)
    }
    handleCloseAddCard()
  }

  const handleSave = (): void => {
    if (!member?.membershipTierId || saving) return

    const cycle = CYCLE_BY_FREQUENCY[frequency] ?? 'Monthly'
    const amount =
      member.paymentCycle === cycle
        ? (member.recurringAmount ?? MEMBERSHIP_CYCLE_AMOUNT[cycle])
        : MEMBERSHIP_CYCLE_AMOUNT[cycle]
    const methodLabel = selectedMethod
      ? formatAutoPayLabel(selectedMethod)
      : 'Debit card - visa ending in 4553'

    setSaving(true)
    void dispatch(
      updateMembershipMember({
        membershipMemberId: member.id,
        membershipTierId: member.membershipTierId,
        paymentCycle: frequency === 'annual' ? 'annual' : 'monthly',
        expiryDate: toApiDate(member.expiryDate),
        recurringAmount: amount,
        autoPayMethod: methodLabel,
      }),
    ).then((result) => {
      setSaving(false)

      if (updateMembershipMember.rejected.match(result)) {
        showToast(
          createFailedToast(
            'Unable to update payment',
            result.payload || 'Failed to update payment details.',
          ),
        )
        return
      }

      showToast(createPaymentDetailsUpdatedToast(memberName))
      onSaved?.({
        member: result.payload,
        billingLabel: formatSavedBilling(result.payload),
        autoPayMethod: methodLabel,
      })
      handleClose()
    })
  }

  return (
    <>
      <Modal
        open={open && !isAddCardOpen}
        onClose={handleClose}
        size="md"
        title="Manage recurring payment"
        className="manage-recurring-payment-modal"
        primaryLabel="Save"
        primaryDisabled={saving}
        secondaryLabel="Cancel"
        onPrimaryClick={handleSave}
        onSecondaryClick={handleClose}
      >
        <div className="manage-recurring-payment__section">
          <p className="manage-recurring-payment__section-title">
            Choose a billing frequency
          </p>
          <SelectionBoxGroup
            className="manage-recurring-payment__option-group"
            name="manage-billing-frequency"
            value={frequency}
            onChange={setFrequency}
          >
            {MEMBERSHIP_BILLING_FREQUENCIES.map((option) => (
              <SelectionBox
                key={option.value}
                value={option.value}
                title={option.title}
                description={option.description}
                showSelectIndicator={false}
              />
            ))}
          </SelectionBoxGroup>
        </div>
        <div className="manage-recurring-payment__section">
          <div className="manage-recurring-payment__section-header">
            <p className="manage-recurring-payment__section-title">
              Choose a auto-pay method
            </p>
            <Button variant="primary" size="xs" onClick={handleOpenAddCard}>
              Add card
            </Button>
          </div>
          <SelectionBoxGroup
            className="manage-recurring-payment__option-group"
            name="manage-auto-pay-method"
            value={autoPay}
            onChange={setAutoPay}
          >
            {methods.map((option) => (
              <SelectionBox
                key={option.value}
                value={option.value}
                title={option.title}
                description={renderAccountDescription(option)}
                showSelectIndicator={false}
              />
            ))}
          </SelectionBoxGroup>
        </div>
      </Modal>
      <Modal
        open={open && isAddCardOpen}
        onClose={handleCloseAddCard}
        size="sm"
        title="Add payment method"
        className="manage-payment-add-modal"
        bodyClassName="manage-payment-add-modal__body"
        primaryLabel="Add payment method"
        secondaryLabel=""
        primaryDisabled={!isAddCardValid}
        onPrimaryClick={handleAddCard}
      >
        <div className="manage-payment-add__panel">
          <div className="manage-payment-add__method">
            <i
              className="mgc mgc_bank_card_fill manage-payment-add__method-icon"
              aria-hidden
            />
            Card
          </div>
          <div className="manage-payment-add__fields">
            <InputField
              label="Card number"
              variant={PLAIN_TEXT_VARIANT}
              placeholder="1234 1234 1234 1234"
              value={cardNumber}
              onChange={(event) =>
                setCardNumber(formatInputFieldCardNumber(event.target.value))
              }
              trailingIcon={
                cardBrand === 'unknown' ? undefined : (
                  <img
                    className="manage-payment-add__card-brand"
                    src={getCardLogo(cardBrand)}
                    alt=""
                    width={32}
                    height={20}
                  />
                )
              }
            />
            <div className="manage-payment-add__row">
              <InputField
                label="Expiration date"
                variant={PLAIN_TEXT_VARIANT}
                placeholder="MM / YY"
                value={expiry}
                onChange={(event) =>
                  setExpiry(formatExpiry(event.target.value))
                }
              />
              <InputField
                label="Security code"
                variant={PLAIN_TEXT_VARIANT}
                placeholder="CVC"
                maxLength={4}
                value={cvc}
                onChange={(event) =>
                  setCvc(event.target.value.replace(/\D/g, '').slice(0, 4))
                }
              />
            </div>
            <div className="manage-payment-add__row">
              <Select
                label="Country"
                options={MANAGE_PAYMENT_COUNTRIES}
                value={country}
                onChange={handleCountryChange}
              />
              <InputField
                label="ZIP code"
                variant={PLAIN_TEXT_VARIANT}
                placeholder="12345"
                maxLength={5}
                value={zip}
                onChange={(event) =>
                  setZip(event.target.value.replace(/\D/g, '').slice(0, 5))
                }
              />
            </div>
            <p className="manage-payment-add__legal">
              By providing your card information, you allow Sunshine Family
              Dentistry to charge your card for future payments in accordance
              with their terms.
            </p>
            <Checkbox
              label="Make this my default card"
              checked={isDefaultCard}
              onChange={(event) => setIsDefaultCard(event.target.checked)}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ManageRecurringPaymentModal
